import React, { useState, useEffect, useCallback, useRef } from 'react';
import { C, card, sectionTitle, filterPill, kpiCard, kpiIcon, toggleGroup, toggleBtn, th, td, tdR } from '../../styles';
import { getBookingSummary, getBookingTrend, getBookingFilterOptions, getVisualPrefs, setVisualPref } from '../../services/api';
import api from '../../services/api';
const ReactECharts = require('echarts-for-react').default;

// ─── helpers ──────────────────────────────────────────────────────────────────

const parseLocalDate = (str) => {
  if (!str) return null;
  const s = String(str);
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(+iso[1], +iso[2]-1, +iso[3]);
  const d = new Date(s);
  if (!isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return null;
};

const fmtDate = (str, opts) => {
  const d = parseLocalDate(str);
  return d ? d.toLocaleDateString('en-GB', opts) : str;
};

const toISOKey = (str) => {
  if (!str) return '';
  const m = String(str).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const pad = n => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  }
  return String(str);
};

const isMultiYear = (isoKeys) => {
  if (!isoKeys.length) return false;
  return new Set(isoKeys.map(p => String(p).slice(0,4))).size > 1;
};

const buildMarkArea = (isoKeys) => {
  if (!isoKeys.length) return [];
  const groups = {};
  isoKeys.forEach(p => {
    const yr = String(p).slice(0,4);
    if (!groups[yr]) groups[yr] = { start:p, end:p };
    groups[yr].end = p;
  });
  return Object.entries(groups).map(([,{start,end}],i) => [
    { xAxis:start, itemStyle:{ color:i%2===0?'rgba(255,255,255,0.06)':'rgba(255,255,255,0.02)' }, label:{show:false} },
    { xAxis:end }
  ]);
};

const generateFilledPeriodKeys = (sortedISO, granularity) => {
  if (!sortedISO.length) return sortedISO;
  const start = parseLocalDate(sortedISO[0]);
  const end   = parseLocalDate(sortedISO[sortedISO.length - 1]);
  if (!start || !end) return sortedISO;
  const pad = n => String(n).padStart(2, '0');
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const result = [];
  const cur = new Date(start);
  while (cur <= end) {
    result.push(fmt(cur));
    if (granularity === 'monthly') cur.setMonth(cur.getMonth() + 1);
    else if (granularity === 'weekly') cur.setDate(cur.getDate() + 7);
    else cur.setDate(cur.getDate() + 1);
  }
  return result;
};

const makeLabelFmt = (period, multiYear) => (v) => {
  try {
    const d = parseLocalDate(v);
    if (!d) return v;
    if (period==='monthly') {
      const mmm = d.toLocaleDateString('en-GB', { month:'short' });
      if (multiYear && d.getMonth()===0) return mmm+'\n'+d.getFullYear();
      return mmm;
    }
    const label = d.toLocaleDateString('en-GB', { day:'numeric', month:'short' });
    if (multiYear && d.getDate()<=7 && d.getMonth()===0) return label+'\n'+d.getFullYear();
    return label;
  } catch { return v; }
};

// ─── constants ────────────────────────────────────────────────────────────────

const STATUS_PALETTE = [
  '#FBBF24','#60A5FA','#34D399','#F87171','#A78BFA',
  '#F472B6','#38BDF8','#FB923C','#4ADE80','#E879F9','#94A3B8','#FDE68A',
];

// ─── custom legend helpers ────────────────────────────────────────────────────

const useChartLegend = (items) => {
  const [selected, setSelected] = React.useState(() => new Set(items));
  const itemsKey = items.join('||');
  React.useEffect(() => { setSelected(new Set(items)); }, [itemsKey]); // eslint-disable-line
  const toggle = (chartRef, name) => {
    chartRef?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendToggleSelect', name });
    setSelected(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  };
  const selectAll = (chartRef) => {
    chartRef?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendAllSelect' });
    setSelected(new Set(items));
  };
  const resetAll = (chartRef) => {
    const c = chartRef?.current?.getEchartsInstance?.();
    if (c) { c.dispatchAction({ type: 'legendAllSelect' }); c.dispatchAction({ type: 'legendInverseSelect' }); }
    setSelected(new Set());
  };
  return { selected, toggle, selectAll, resetAll };
};

const ChartLegend = ({ items, colors, chartRef, selected, onToggle, onSelectAll, onReset }) => (
  <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'4px 10px', padding:'6px 0 0' }}>
    <button onClick={() => onSelectAll(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.accent}`, borderRadius:'4px', color:C.accent, cursor:'pointer', flexShrink:0 }}>
      All
    </button>
    <button onClick={() => onReset(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer', flexShrink:0 }}>
      Reset
    </button>
    {items.map((name, i) => {
      const isSel = selected.has(name);
      return (
        <div key={name} onClick={() => onToggle(chartRef, name)}
          style={{ display:'flex', alignItems:'center', gap:'3px', cursor:'pointer', opacity: isSel ? 1 : 0.3, flexShrink:0 }}>
          <div style={{ width:'7px', height:'7px', borderRadius:'50%', background: colors[i % colors.length] }}/>
          <span style={{ fontSize:'9px', color: C.textSec }}>{name}</span>
        </div>
      );
    })}
  </div>
);

const QUICK = [
  { id: 'yesterday',       label: 'Yesterday'        },
  { id: 'this_year',       label: 'This Year'         },
  { id: 'this_month',      label: 'This Month'        },
  { id: 'this_week',       label: 'This Week'         },
  { id: 'last_year',       label: 'Last Year'         },
  { id: 'last_month',      label: 'Last Month'        },
  { id: 'last_week',       label: 'Last Week'         },
  { id: 'last_year_month', label: "Last Year's Month" },
  { id: 'custom',          label: 'Custom'            },
  { id: 'all',             label: 'All Time'          },
];

// ─── sub-components ───────────────────────────────────────────────────────────

const KPICard = ({ title, value, icon, iconBg, sub }) => (
  <div style={kpiCard}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'5px' }}>
      <div style={kpiIcon(iconBg)}>{icon}</div>
    </div>
    <p style={{ fontSize:'11px', color:C.textSec, margin:'0 0 4px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'0.5px' }}>{title}</p>
    <p style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, margin:0, lineHeight:1.2 }}>{value}</p>
    {sub && <p style={{ fontSize:'10px', color:C.textMuted, margin:'3px 0 0' }}>{sub}</p>}
  </div>
);

const CollapsibleSection = ({ visKey, title, collapsed, onToggle, headerRight, children }) => (
  <div style={{ ...card, marginBottom:'12px' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:collapsed?0:'8px' }}>
      <p style={{ ...sectionTitle, marginBottom:0 }}>{title}</p>
      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
        {!collapsed && headerRight}
        <button onClick={()=>onToggle(visKey)}
          style={{ fontSize:'10px', color:C.textSec, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'3px 8px', cursor:'pointer' }}>
          {collapsed ? 'Expand' : 'Collapse'}
        </button>
      </div>
    </div>
    {!collapsed && children}
  </div>
);

// ─── MultiSelect: supports {key, label} options, with search, pending-safe ────
const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen]     = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = (val) => {
    const next = selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val];
    onChange(next);
  };

  const filtered = (options || []).filter(opt => {
    const lbl = typeof opt === 'object' ? (opt.label || opt.name || '') : String(opt || '');
    return String(lbl).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div ref={ref} style={{ position:'relative', flex:1, minWidth:'130px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', padding:'4px 10px', background:C.bgSurface, border:`1px solid ${selected.length ? C.accent : C.border}`, borderRadius:'8px', fontSize:'11px', color:selected.length ? C.accent : C.textMuted, textAlign:'left', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
      >
        <span>{label}{selected.length ? ` (${selected.length})` : ''}</span>
        <span style={{ fontSize:'9px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:300, background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', marginTop:'4px', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>
          <div style={{ padding:'6px 8px', borderBottom:`1px solid ${C.border}` }}>
            <input
              autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..." onClick={e => e.stopPropagation()}
              style={{ width:'100%', padding:'5px 8px', background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'6px', fontSize:'11px', color:C.textPrimary, outline:'none', boxSizing:'border-box' }}
            />
          </div>
          <div style={{ maxHeight:'150px', overflowY:'auto' }}>
            {filtered.length === 0 && <p style={{ padding:'8px 10px', fontSize:'11px', color:C.textMuted }}>No results</p>}
            {filtered.map((opt, i) => {
              const val = typeof opt === 'object' ? (opt.key || opt.code || String(opt)) : String(opt || '');
              const lbl = typeof opt === 'object' ? (opt.label || opt.name || val) : String(opt || '');
              const isSel = selected.includes(val);
              return (
                <div key={i} onClick={() => toggle(val)} style={{ padding:'7px 10px', fontSize:'11px', color:isSel ? C.accent : C.textSec, background:isSel ? C.accentDim : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
                  <span style={{ width:'12px', height:'12px', border:`1px solid ${isSel ? C.accent : C.border}`, borderRadius:'3px', background:isSel ? C.accent : 'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {isSel && <span style={{ color:'#000', fontSize:'8px' }}>✓</span>}
                  </span>
                  {lbl}
                </div>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div style={{ padding:'6px 8px', borderTop:`1px solid ${C.border}` }}>
              <button onClick={() => { onChange([]); setOpen(false); }}
                style={{ fontSize:'10px', color:C.danger, background:'none', border:'none', cursor:'pointer' }}>Clear All</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SortTh = ({ col, label, sortCol, sortDir, onSort, style }) => (
  <th onClick={() => onSort(col)}
    style={{ ...style, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
    {label}
    <span style={{ marginLeft:'4px', opacity: sortCol === col ? 1 : 0.3, fontSize:'9px' }}>
      {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
    </span>
  </th>
);

const useSortedData = (data, defaultCol, defaultDir = 'desc') => {
  const [sortCol, setSortCol] = useState(defaultCol);
  const [sortDir, setSortDir] = useState(defaultDir);
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const sorted = [...data].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    const an = parseFloat(av), bn = parseFloat(bv);
    const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av || '').localeCompare(String(bv || ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return { sorted, sortCol, sortDir, toggleSort };
};

// ─── main component ───────────────────────────────────────────────────────────

const AppointmentTracker = () => {
  const [activePeriod, setAP]           = useState(() => localStorage.getItem('ccs_period') || 'this_month');
  const setPeriod = (p) => { localStorage.setItem('ccs_period', p); setAP(p); };
  // ── Branch multi-select (boss + area_manager only, persists in localStorage) ─
  const [branches,        setBranches]    = useState([]);
  const [selectedBranches, setSelected]  = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; }
  });
  const [pendingBranches, setPending] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; }
  });
  const [showBranchDrop, setShowBranchDrop] = useState(false);
  const openBranchDrop = () => { if (showBranchDrop) { setShowBranchDrop(false); } else { setPending(selectedBranches); setShowBranchDrop(true); } };
  const toggleBranch   = (code) => setPending(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  const applyBranches  = () => { localStorage.setItem('ccs_branch', JSON.stringify(pendingBranches)); setSelected(pendingBranches); setShowBranchDrop(false); };
  const resetBranches  = () => { setPending([]); setSelected([]); localStorage.setItem('ccs_branch', '[]'); setShowBranchDrop(false); };

  // Load available branches for this tenant
  useEffect(() => {
    import('../../services/api').then(({ default: api }) => {
      api.get('/api/branches').then(r => setBranches(r.data.branches || [])).catch(() => {});
    });
  }, []);

    // Compute date range synchronously from period string
  const _computeDates = (period) => {
    const now = new Date();
    const _p  = n => String(n).padStart(2, '0');
    const _f  = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    if (period === 'yesterday')      { const y = new Date(now); y.setDate(y.getDate()-1); return [_f(y), _f(y)]; }
    if (period === 'this_month')     return [_f(new Date(now.getFullYear(), now.getMonth(), 1)), _f(now)];
    if (period === 'this_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day+1); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'this_year')      return [_f(new Date(now.getFullYear(), 0, 1)), _f(now)];
    if (period === 'last_year')      return [_f(new Date(now.getFullYear()-1, 0, 1)), _f(new Date(now.getFullYear()-1, 11, 31))];
    if (period === 'last_month')     return [_f(new Date(now.getFullYear(), now.getMonth()-1, 1)), _f(new Date(now.getFullYear(), now.getMonth(), 0))];
    if (period === 'last_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day-6); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'last_year_month'){ const m = now.getMonth(), ly = now.getFullYear()-1; return [_f(new Date(ly,m,1)), _f(new Date(ly,m+1,0))]; }
    if (period === 'custom')        return [localStorage.getItem('ccs_from')||'', localStorage.getItem('ccs_to')||''];
    return ['', ''];
  };
  const _initPeriod = localStorage.getItem('ccs_period') || 'this_month';
  const [_initFrom, _initTo] = _computeDates(_initPeriod);
  const _initPendFrom = _initPeriod === 'custom' ? (localStorage.getItem('ccs_from')||'') : '';
  const _initPendTo   = _initPeriod === 'custom' ? (localStorage.getItem('ccs_to')||'')   : '';
const [dateFrom, setDateFrom]         = useState(_initFrom);
  const [dateTo,   setDateTo]           = useState(_initTo);
  const [pendingFrom, setPF]            = useState(_initPendFrom);
  const [pendingTo,   setPT]            = useState(_initPendTo);

  // Filters: pending = what's shown in the panel, active = what's been applied
  const [filtersOpen, setFO]            = useState(false);
  const [filterOpts,  setFilterOpts]    = useState({ staff:[], customers:[] });
  const [activeFilters, setAF]          = useState({ staff:[], customer:[] });
  const [pendingFilters, setPF_]        = useState({ staff:[], customer:[] });

  const trendChartRef  = useRef(null);
  const staffChartRef  = useRef(null);
  const donutRef       = useRef(null);
  const billedDonutRef   = useRef(null);
  const unbilledDonutRef = useRef(null);
  const [selServiceBar, setSelServiceBar] = useState(null);
  const resetLegend = (ref) => {
    const chart = ref.current?.getEchartsInstance();
    if (!chart) return;
    chart.dispatchAction({ type: 'legendAllSelect' });
    chart.dispatchAction({ type: 'legendInverseSelect' });
  };

  const [kpis,      setKpis]            = useState(null);
  const [trend,     setTrend]           = useState([]);
  const [byStaff,   setByStaff]         = useState([]);
  const [byService, setByService]       = useState([]);
  const [byStatus,        setByStatus]        = useState([]);
  const [byStatusBilled,  setByStatusBilled]  = useState([]);
  const [byStatusUnbilled,setByStatusUnbilled]= useState([]);
  const [loading,   setLoading]         = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendPeriod, setTP]            = useState(() => localStorage.getItem('ccs_gran') || 'daily');
  const setGran = (g) => { localStorage.setItem('ccs_gran', g); setTP(g); };

  // Force monthly for All Time
  useEffect(() => {
    if (activePeriod === 'all' && trendPeriod !== 'monthly') setGran('monthly');
  }, [activePeriod]);
  const [collapsed, setCollapsed]       = useState({});

  useEffect(() => {
    getVisualPrefs().then(r => setCollapsed(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (selectedBranches.length > 0) p.branch_code = selectedBranches.join(',');
    getBookingFilterOptions(p).then(r => setFilterOpts(r.data || {})).catch(() => {});
  }, [dateFrom, dateTo, selectedBranches]);

  useEffect(() => {
    if (activePeriod === 'custom') return;
    const now = new Date();
    const _p = n => String(n).padStart(2, '0');
    const _f = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    let from = '', to = '';
    if (activePeriod === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      from = to = _f(y);
    } else if (activePeriod === 'this_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth(), 1));
      to   = _f(now);
    } else if (activePeriod === 'this_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      const e = new Date(d); e.setDate(d.getDate() + 6);
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'this_year') {
      from = _f(new Date(now.getFullYear(), 0, 1));
      to   = _f(now);
    } else if (activePeriod === 'last_year') {
      from = _f(new Date(now.getFullYear() - 1, 0, 1));
      to   = _f(new Date(now.getFullYear() - 1, 11, 31));
    } else if (activePeriod === 'last_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      to   = _f(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (activePeriod === 'last_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day - 6); // last Monday
      const e = new Date(d); e.setDate(d.getDate() + 6); // last Sunday
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'last_year_month') {
      const m = now.getMonth();
      const ly = now.getFullYear() - 1;
      from = _f(new Date(ly, m, 1));
      to   = _f(new Date(ly, m + 1, 0));
    }
    setDateFrom(from);
    setDateTo(to);
  }, [activePeriod]);

  useEffect(() => { loadMain(); }, [dateFrom, dateTo, activeFilters, selectedBranches]);
  useEffect(() => { loadTrend(); }, [trendPeriod, dateFrom, dateTo, activeFilters, selectedBranches]);

  const mainParams = useCallback(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (activeFilters.staff.length)    p.staff    = activeFilters.staff.join(',');
    if (activeFilters.customer.length) p.customer = activeFilters.customer.join(',');
    if (selectedBranches.length > 0) p.branch_code = selectedBranches.join(',');
    return p;
  }, [dateFrom, dateTo, activeFilters, selectedBranches]);

  const loadMain = async () => {
    setLoading(true);
    try {
      const res = await getBookingSummary(mainParams());
      const d = res.data;
      setKpis(d.kpis || null);
      setByStaff(d.by_staff || []);
      setByService(d.by_service || []);
      setByStatus(d.by_status || []);
      setByStatusBilled(d.by_status_billed || []);
      setByStatusUnbilled(d.by_status_unbilled || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadTrend = async () => {
    setTrendLoading(true);
    try {
      const res = await getBookingTrend({ ...mainParams(), period: trendPeriod });
      setTrend(res.data.data || []);
    } catch(e) { console.error(e); }
    finally { setTrendLoading(false); }
  };

  const applyCustom = () => {
    localStorage.setItem('ccs_from', pendingFrom);
    localStorage.setItem('ccs_to', pendingTo);
    setDateFrom(pendingFrom);
    setDateTo(pendingTo);
  };

  const toggleCollapse = useCallback((key) => {
    const next = !collapsed[key];
    setCollapsed(prev => ({ ...prev, [key]:next }));
    setVisualPref({ visual_key:key, is_collapsed:next }).catch(() => {});
  }, [collapsed]);

  // ── derived ───────────────────────────────────────────────────────────────

  const total            = kpis?.total_bookings || 0;
  const cancelled        = kpis?.cancelled      || 0;
  const noShow           = kpis?.no_show        || 0;
  const cancellationRate = total > 0 ? (((cancelled + noShow) / total) * 100).toFixed(1) : '0';

  const statusColorMap = {};
  byStatus.forEach((s, i) => { statusColorMap[s.status_clean] = STATUS_PALETTE[i % STATUS_PALETTE.length]; });

  const trendMap = {};
  trend.forEach(t => {
    const pk = toISOKey(t.period);
    if (!trendMap[pk]) trendMap[pk] = { total:0 };
    trendMap[pk][t.status_clean] = parseInt(t.count || 0);
    trendMap[pk].total = (trendMap[pk].total || 0) + parseInt(t.count || 0);
  });

  const periodKeys      = generateFilledPeriodKeys(Object.keys(trendMap).sort(), trendPeriod);
  const multiYear       = isMultiYear(periodKeys);
  const markArea        = buildMarkArea(periodKeys);
  const labelFmt        = makeLabelFmt(trendPeriod, multiYear);
  // Donut/status-breakdown uses raw status_clean from booking system
  const dynamicStatuses = byStatus.map(s => s.status_clean);

  // Trend chart uses derived_status — fixed 4 buckets, consistent colours
  const DERIVED_STATUS_COLORS = {
    'Arrived':   '#34D399',
    'Cancelled': '#F87171',
    'No-Show':   '#FB923C',
    'Upcoming':  '#60A5FA',
  };
  const trendStatuses = ['Arrived', 'Cancelled', 'No-Show', 'Upcoming'].filter(
    s => trend.some(t => t.status_clean === s)
  );
  const trendItems = [...trendStatuses, 'Total'];

  const trendLegend       = useChartLegend(trendStatuses);
  const donutLegend       = useChartLegend(dynamicStatuses);
  const staffBarLegend    = useChartLegend(['Bookings', 'Cancelled', 'No-Show']);
  const billedStatuses    = byStatusBilled.map(s => s.status_clean);
  const billedDonutLegend = useChartLegend(billedStatuses);
  const unbilledStatuses    = byStatusUnbilled.map(s => s.status_clean);
  const unbilledDonutLegend = useChartLegend(unbilledStatuses);

  // ── DONUT ─────────────────────────────────────────────────────────────────

  const donutOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger:'item', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{ color:C.textPrimary, fontSize:11 }, formatter: p => `${p.name}: ${p.value} (${p.percent}%)` },
    legend: { show: false, data: dynamicStatuses },
    series: [{ type:'pie', radius:['38%','65%'], center:['50%','50%'],
      data: byStatus.map(s => ({ name:s.status_clean, value:parseInt(s.count||0), itemStyle:{ color:statusColorMap[s.status_clean]||C.textMuted } })),
      label:{ show:false } }]
  };

  // ── TREND ─────────────────────────────────────────────────────────────────

  const trendOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger:'axis', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{ color:C.textPrimary, fontSize:11 },
      formatter: (params) => {
        const lbl = fmtDate(params[0]?.name, trendPeriod === 'monthly'
          ? { month:'short', year:'numeric' } : { day:'numeric', month:'short', year:multiYear?'numeric':undefined });
        return lbl + '<br/>' + params.filter(p => p.value > 0).map(p =>
          `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>${Number(p.value||0).toLocaleString()}</b>`
        ).join('<br/>');
      },
    },
    legend: { show: false },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: { type:'category', data:periodKeys, axisLine:{ lineStyle:{ color:C.border } }, axisTick:{ show:false }, axisLabel:{ color:C.textMuted, fontSize:9, rotate:multiYear?30:0, formatter:labelFmt, interval:'auto' } },
    yAxis: { type:'value', splitLine:{ lineStyle:{ color:C.bgElevated } }, axisLabel:{ color:C.textMuted, fontSize:10 } },
    series: [
      ...trendStatuses.map((status) => ({
        name: status, type:'line', smooth:true,
        symbol:'circle', symbolSize:periodKeys.length>60?2:3, showSymbol:periodKeys.length<=5,
        data: periodKeys.map(pk => (trendMap[pk] || {})[status] || 0),
        lineStyle:{ color:DERIVED_STATUS_COLORS[status] || C.accent, width:2 },
        itemStyle:{ color:DERIVED_STATUS_COLORS[status] || C.accent },
        markArea: status === trendStatuses[0] && markArea.length ? { silent:true, data:markArea } : undefined,
      })),
      { name:'Total', type:'line', smooth:true,
        symbol:'circle', symbolSize:2, showSymbol:periodKeys.length<=5,
        data: periodKeys.map(pk => (trendMap[pk] || {}).total || 0),
        lineStyle:{ color:'rgba(255,255,255,0.5)', width:1.5, type:'dashed' },
        itemStyle:{ color:'rgba(255,255,255,0.5)' } },
    ],
  };

  // ── STAFF CHART ───────────────────────────────────────────────────────────

  const staffChartOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger:'axis', axisPointer:{ type:'shadow' }, backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{ color:C.textPrimary, fontSize:11 },
      formatter: (params) => {
        const bookings  = params.find(p => p.seriesName === 'Bookings')?.value  || 0;
        const cancelled = params.find(p => p.seriesName === 'Cancelled')?.value || 0;
        const noShow    = params.find(p => p.seriesName === 'No-Show')?.value   || 0;
        return params[0]?.axisValue + '<br/>' +
          `<span style="color:#34D399">■</span> Bookings: <b>${bookings}</b><br/>` +
          `<span style="color:#F87171">■</span> Cancelled: <b>${cancelled}</b><br/>` +
          `<span style="color:#FB923C">■</span> No-Show: <b>${noShow}</b><br/>` +
          `<b>Total: ${bookings + cancelled + noShow}</b>`;
      },
    },
    legend: { show: false, data: ['Bookings', 'Cancelled', 'No-Show'] },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: { type:'category', data:byStaff.map(s => s.hr_name || s.hr_code), axisLine:{ lineStyle:{ color:C.border } }, axisTick:{ show:false }, axisLabel:{ color:C.textMuted, fontSize:9, rotate:30, interval:0, overflow:'truncate', width:60 } },
    yAxis: { type:'value', splitLine:{ lineStyle:{ color:C.bgElevated } }, axisLabel:{ color:C.textMuted, fontSize:10 } },
    series: [
      {
        name:'Bookings', type:'bar', stack:'total',
        data: byStaff.map(s => Math.max(0, parseInt(s.total_bookings||0) - parseInt(s.cancelled||0) - parseInt(s.no_show||0))),
        itemStyle:{ color:'#34D399', borderRadius:[0,0,0,0] },
      },
      {
        name:'No-Show', type:'bar', stack:'total',
        data: byStaff.map(s => parseInt(s.no_show||0)),
        itemStyle:{ color:'#FB923C', borderRadius:[0,0,0,0] },
      },
      {
        name:'Cancelled', type:'bar', stack:'total',
        data: byStaff.map(s => parseInt(s.cancelled||0)),
        itemStyle:{ color:'#F87171', borderRadius:[4,4,0,0] },
      },
    ],
  };

  // ── SERVICE CHART ─────────────────────────────────────────────────────────

  const serviceChartOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger:'axis', axisPointer:{ type:'shadow' }, backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{ color:C.textPrimary, fontSize:11 } },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: { type:'category', data:byService.slice(0,12).map(s => (s.service_code || 'Unknown').substring(0,14)), axisLine:{ lineStyle:{ color:C.border } }, axisTick:{ show:false }, axisLabel:{ color:C.textMuted, fontSize:9, rotate:30, interval:0 } },
    yAxis: { type:'value', splitLine:{ lineStyle:{ color:C.bgElevated } }, axisLabel:{ color:C.textMuted, fontSize:10 } },
    series: [{ type:'bar', data:byService.slice(0,12).map((s,i) => ({ value:parseInt(s.total_bookings||0), itemStyle:{ color:C.purple, opacity: selServiceBar === null ? 1-i*0.06 : (selServiceBar === i ? 1 : 0.2), borderRadius:[4,4,0,0] } })) }]
  };

  // ── table sort (component-level, Rules of Hooks) ──────────────────────────
  const staffWithRate = byStaff.map(s => ({
    ...s,
    cancel_rate:  s.total_bookings > 0 ? (parseInt(s.cancelled  || 0) / parseInt(s.total_bookings)) * 100 : 0,
    noshow_rate:  s.total_bookings > 0 ? (parseInt(s.no_show    || 0) / parseInt(s.total_bookings)) * 100 : 0,
  }));
  const { sorted: staffSorted, sortCol: sCol, sortDir: sDir, toggleSort: sSort } = useSortedData(staffWithRate, 'total_bookings');

  // ── period toggle ─────────────────────────────────────────────────────────

  const periodToggle = (
    <div style={toggleGroup}>
      {['daily','weekly','monthly'].map(p => {
        const disabled = activePeriod === 'all' && p !== 'monthly';
        return (
          <button key={p} style={{ ...toggleBtn(trendPeriod === p), opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer' }}
            onClick={() => !disabled && setGran(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        );
      })}
    </div>
  );

  const activeFiltersCount = activeFilters.staff.length + activeFilters.customer.length;

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'40px', height:'40px', border:`3px solid ${C.bgElevated}`, borderTopColor:C.blue, borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
        <p style={{ color:C.textMuted, fontSize:'13px' }}>Loading booking data...</p>
      </div>
    </div>
  );

  return (
    <div style={{ padding:'16px', paddingBottom:'32px' }}>

      {/* ── QUICK FILTERS ── */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
        {QUICK.map(q => <button key={q.id} onClick={() => setPeriod(q.id)} style={filterPill(activePeriod === q.id)}>{q.label}</button>)}
      </div>
          {/* Branch multi-select — boss, area_manager, superadmin only */}
          {(() => {
            try {
              const u = JSON.parse(localStorage.getItem('user') || '{}');
              if (!['boss','area_manager','superadmin'].includes(u.role)) return null;
            } catch { return null; }
            if (branches.length === 0) return null;
            const label = selectedBranches.length === 0 ? 'All Branches'
              : selectedBranches.length === 1 ? (branches.find(b => b.code === selectedBranches[0])?.name || selectedBranches[0])
              : `${selectedBranches.length} Branches`;
            return (
              <div style={{ marginBottom:'10px', position:'relative', display:'inline-block' }}>
                <button onClick={openBranchDrop}
                  style={{ ...filterPill(selectedBranches.length > 0), display:'flex', alignItems:'center', gap:'6px', padding:'6px 14px' }}>
                  🏢 {label} <span style={{ fontSize:'10px', opacity:0.7 }}>{showBranchDrop ? '▴' : '▾'}</span>
                </button>
                {showBranchDrop && (
                  <div style={{ position:'absolute', top:'38px', left:0, zIndex:999,
                    background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'12px',
                    padding:'8px', minWidth:'180px', boxShadow:'0 8px 32px rgba(0,0,0,0.4)' }}>
                    {branches.map(b => (
                      <label key={b.code} style={{ display:'flex', alignItems:'center', gap:'8px',
                        padding:'7px 10px', cursor:'pointer', borderRadius:'8px', fontSize:'13px',
                        color: pendingBranches.includes(b.code) ? C.accent : C.textSec,
                        background: pendingBranches.includes(b.code) ? 'rgba(168,224,99,0.1)' : 'transparent' }}>
                        <input type="checkbox" checked={pendingBranches.includes(b.code)}
                          onChange={() => toggleBranch(b.code)} style={{ accentColor: C.accent }} />
                        {b.name || b.code}
                      </label>
                    ))}
                    <div style={{ borderTop:`1px solid ${C.border}`, marginTop:'6px', paddingTop:'6px', display:'flex', flexDirection:'column', gap:'4px' }}>
                      <button onClick={applyBranches}
                        style={{ width:'100%', background:C.accent, border:'none', borderRadius:'6px',
                          color:'#000', fontSize:'12px', fontWeight:'700', cursor:'pointer', padding:'6px 0' }}>
                        Apply
                      </button>
                      <button onClick={resetBranches}
                        style={{ width:'100%', background:'transparent', border:'none',
                          color:C.textMuted, fontSize:'12px', cursor:'pointer', padding:'4px 0' }}>
                        ↩ Reset to All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

      {/* ── CUSTOM DATE RANGE ── */}
      {activePeriod === 'custom' && (
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', alignItems:'flex-end' }}>
          {[['From', pendingFrom, setPF], ['To', pendingTo, setPT]].map(([label, val, setter]) => (
            <div key={label} style={{ flex:1 }}>
              <p style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>{label}</p>
              <input type="date" value={val} onChange={e => setter(e.target.value)}
                style={{ width:'100%', padding:'8px 10px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'12px', color:val ? C.textPrimary : C.textMuted, outline:'none', boxSizing:'border-box' }}
              />
            </div>
          ))}
          <button onClick={applyCustom} disabled={!pendingFrom || !pendingTo}
            style={{ padding:'8px 14px', background:pendingFrom && pendingTo ? C.accent : C.bgElevated, color:pendingFrom && pendingTo ? '#000' : C.textMuted, border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor:pendingFrom && pendingTo ? 'pointer' : 'default', flexShrink:0 }}>
            Apply
          </button>
        </div>
      )}

      {/* ── ADVANCED FILTERS ── */}
      <div style={{ marginBottom:'12px' }}>
        <button onClick={() => setFO(o => !o)} style={{ ...filterPill(activeFiltersCount > 0), display:'flex', alignItems:'center', gap:'6px' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          Filters{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
          <span style={{ fontSize:'9px' }}>{filtersOpen ? '▲' : '▼'}</span>
        </button>

        {filtersOpen && (
          <div style={{ marginTop:'8px', padding:'12px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'12px' }}>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', alignItems:'flex-start' }}>

              {/* Staff - key=hr_code, label=name */}
              <MultiSelect
                label="Staff"
                options={(filterOpts.staff || []).map(s => ({ key: s.code, label: s.name }))}
                selected={pendingFilters.staff}
                onChange={v => setPF_(f => ({ ...f, staff: v }))}
              />

              {/* Customer - key=member_code, label=customer_name */}
              <MultiSelect
                label="Customer"
                options={(filterOpts.customers || []).map(c => ({ key: c.member_code, label: c.customer_name }))}
                selected={pendingFilters.customer}
                onChange={v => setPF_(f => ({ ...f, customer: v }))}
              />

              <button
                onClick={() => {
                  const empty = { staff:[], customer:[] };
                  setPF_(empty);
                  setAF(empty);
                  setFO(false);
                }}
                style={{ alignSelf:'flex-end', fontSize:'11px', color:C.danger, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'5px 10px', cursor:'pointer', whiteSpace:'nowrap' }}
              >
                Reset All
              </button>
            </div>

            <div style={{ marginTop:'10px', display:'flex', justifyContent:'flex-end' }}>
              <button
                onClick={() => { setAF({ ...pendingFilters }); setFO(false); }}
                style={{ padding:'7px 20px', background:C.accent, color:'#000', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── KPIs ── */}
      <CollapsibleSection visKey="at_kpi" title="Summary" collapsed={!!collapsed['at_kpi']} onToggle={toggleCollapse}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
          <KPICard title="Total Bookings" value={total.toLocaleString()} iconBg={C.blueDim}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke={C.blue} strokeWidth="2"/><path d="M16 2v4M8 2v4M3 10h18" stroke={C.blue} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Arrived" value={(kpis?.arrived||0).toLocaleString()} iconBg={C.accentDim}
            sub={`${(kpis?.arrival_rate||0).toFixed(1)}% arrival rate`}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke={C.accent} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Cancelled" value={(cancelled||0).toLocaleString()} iconBg={C.amberDim}
            sub={`${(kpis?.cancel_rate||0).toFixed(1)}% cancel rate`}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.amber} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="No-Show" value={(noShow||0).toLocaleString()} iconBg={C.dangerDim}
            sub={`${(kpis?.noshow_rate||0).toFixed(1)}% no-show rate`}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke={C.danger} strokeWidth="2"/><path d="M8 12h8" stroke={C.danger} strokeWidth="2" strokeLinecap="round"/></svg>} />
        </div>
        {noShow > 0 && (
          <p style={{ fontSize:'10px', color:C.textMuted, marginTop:'8px', padding:'8px 10px', background:C.bgElevated, borderRadius:'8px', lineHeight:'1.5' }}>
            No-Show = booked but no matched payment record within ±1 day.
          </p>
        )}
      </CollapsibleSection>

      {/* ── STATUS BREAKDOWN ── */}
      {byStatus.length > 0 && (
        <CollapsibleSection visKey="at_status" title="Booking Status Breakdown" collapsed={!!collapsed['at_status']} onToggle={toggleCollapse}>
          <ReactECharts ref={donutRef} option={donutOption} style={{ height:'220px' }} />
          <ChartLegend
            items={dynamicStatuses}
            colors={dynamicStatuses.map(s => statusColorMap[s] || C.textMuted)}
            chartRef={donutRef}
            selected={donutLegend.selected}
            onToggle={donutLegend.toggle}
            onSelectAll={donutLegend.selectAll}
            onReset={donutLegend.resetAll}
          />
        </CollapsibleSection>
      )}

      {/* ── BOOKINGS WITH BILL ISSUED — derived_status = Arrived ── */}
      {byStatusBilled.length > 0 && (
        <CollapsibleSection visKey="at_status_billed" title="Bookings with Bill Issued (by Status)" collapsed={!!collapsed['at_status_billed']} onToggle={toggleCollapse}>
          <p style={{ fontSize:'10px', color:C.textMuted, margin:'0 0 8px' }}>
            Only bookings where a matching sale was recorded for the same member on the appointment date.
          </p>
          <ReactECharts ref={billedDonutRef} option={(() => {
            const billedColorMap = {};
            byStatusBilled.forEach((s, i) => { billedColorMap[s.status_clean] = STATUS_PALETTE[i % STATUS_PALETTE.length]; });
            return {
              backgroundColor: 'transparent',
              tooltip: { trigger:'item', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{ color:C.textPrimary, fontSize:11 }, formatter: p => `${p.name}: ${p.value} (${p.percent}%)` },
              legend: { show: false, data: billedStatuses },
              series: [{ type:'pie', radius:['38%','65%'], center:['50%','50%'],
                data: byStatusBilled.map(s => ({ name:s.status_clean, value:parseInt(s.count||0), itemStyle:{ color:billedColorMap[s.status_clean]||C.textMuted } })),
                label:{ show:false } }]
            };
          })()} style={{ height:'220px' }} />
          <ChartLegend
            items={billedStatuses}
            colors={billedStatuses.map((_, i) => STATUS_PALETTE[i % STATUS_PALETTE.length])}
            chartRef={billedDonutRef}
            selected={billedDonutLegend.selected}
            onToggle={billedDonutLegend.toggle}
            onSelectAll={billedDonutLegend.selectAll}
            onReset={billedDonutLegend.resetAll}
          />
        </CollapsibleSection>
      )}

      {/* ── BOOKINGS WITH NO BILL ISSUED — derived_status NOT Arrived/Upcoming ── */}
      {byStatusUnbilled.length > 0 && (
        <CollapsibleSection visKey="at_status_unbilled" title="Bookings with No Bill Issued (by Status)" collapsed={!!collapsed['at_status_unbilled']} onToggle={toggleCollapse}>
          <p style={{ fontSize:'10px', color:C.textMuted, margin:'0 0 8px' }}>
            Past bookings (excluding Upcoming) where no matching payment was found.
          </p>
          <ReactECharts ref={unbilledDonutRef} option={(() => {
            const unbilledColorMap = {};
            byStatusUnbilled.forEach((s, i) => { unbilledColorMap[s.status_clean] = STATUS_PALETTE[i % STATUS_PALETTE.length]; });
            return {
              backgroundColor: 'transparent',
              tooltip: { trigger:'item', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{ color:C.textPrimary, fontSize:11 }, formatter: p => `${p.name}: ${p.value} (${p.percent}%)` },
              legend: { show: false, data: unbilledStatuses },
              series: [{ type:'pie', radius:['38%','65%'], center:['50%','50%'],
                data: byStatusUnbilled.map(s => ({ name:s.status_clean, value:parseInt(s.count||0), itemStyle:{ color:unbilledColorMap[s.status_clean]||C.textMuted } })),
                label:{ show:false } }]
            };
          })()} style={{ height:'220px' }} />
          <ChartLegend
            items={unbilledStatuses}
            colors={unbilledStatuses.map((_, i) => STATUS_PALETTE[i % STATUS_PALETTE.length])}
            chartRef={unbilledDonutRef}
            selected={unbilledDonutLegend.selected}
            onToggle={unbilledDonutLegend.toggle}
            onSelectAll={unbilledDonutLegend.selectAll}
            onReset={unbilledDonutLegend.resetAll}
          />
        </CollapsibleSection>
      )}

      {/* ── BOOKING TREND ── */}
      <CollapsibleSection visKey="at_trend" title="Booking Trend" collapsed={!!collapsed['at_trend']} onToggle={toggleCollapse}
        headerRight={
          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            {trendLoading && (
              <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                <div style={{ width:'8px', height:'8px', border:`2px solid ${C.bgElevated}`, borderTopColor:C.accent, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
                <span style={{ fontSize:'9px', color:C.textMuted }}>Updating...</span>
              </div>
            )}
            {periodToggle}
          </div>
        }
      >
        <ReactECharts ref={trendChartRef} option={trendOption} style={{ height:'240px', width:'100%' }} />
        <ChartLegend
          items={trendItems} colors={[...trendStatuses.map(s => DERIVED_STATUS_COLORS[s] || C.accent), 'rgba(255,255,255,0.5)']} chartRef={trendChartRef}
          selected={trendLegend.selected}
          onToggle={trendLegend.toggle}
          onSelectAll={trendLegend.selectAll}
          onReset={trendLegend.resetAll}
        />
      </CollapsibleSection>

      {/* ── BOOKINGS PER STAFF ── */}
      {byStaff.length > 0 && (
        <CollapsibleSection visKey="at_staff" title="Bookings per Staff" collapsed={!!collapsed['at_staff']} onToggle={toggleCollapse}>
          <ReactECharts ref={staffChartRef} option={staffChartOption} style={{ height:'240px', width:'100%' }} />
          <ChartLegend
            items={['Bookings', 'Cancelled', 'No-Show']}
            colors={['#34D399', '#F87171', '#FB923C']}
            chartRef={staffChartRef}
            selected={staffBarLegend.selected}
            onToggle={staffBarLegend.toggle}
            onSelectAll={staffBarLegend.selectAll}
            onReset={staffBarLegend.resetAll}
          />
        </CollapsibleSection>
      )}

      {/* ── BOOKINGS BY SERVICE ── */}
      {byService.length > 0 && (
        <CollapsibleSection visKey="at_service" title="Bookings by Service" collapsed={!!collapsed['at_service']} onToggle={toggleCollapse}>
          <ReactECharts option={serviceChartOption} style={{ height:'240px' }}
            onEvents={{ click: (p) => setSelServiceBar(prev => prev === p.dataIndex ? null : p.dataIndex) }} />
          {selServiceBar !== null && (
            <button onClick={() => setSelServiceBar(null)}
              style={{ marginTop:'4px', fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer' }}>
              Clear highlight
            </button>
          )}
        </CollapsibleSection>
      )}

      {/* ── STAFF TABLE ── */}
      {byStaff.length > 0 && (
        <CollapsibleSection visKey="at_staff_table" title="Staff Booking Summary" collapsed={!!collapsed['at_staff_table']} onToggle={toggleCollapse}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr>
                  <SortTh col="hr_name"        label="Staff"      sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                  <SortTh col="total_bookings"  label="Total"      sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                  <SortTh col="cancelled"       label="Cancelled"  sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                  <SortTh col="cancel_rate"     label="Cancel %"   sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                  <SortTh col="no_show"         label="No-Show"    sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                  <SortTh col="noshow_rate"     label="No-Show %"  sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                </tr>
              </thead>
              <tbody>
                {staffSorted.map((s, i) => (
                  <tr key={i}>
                    <td style={{ ...td, color:C.textPrimary, fontWeight:'500' }}>{s.hr_name || s.hr_code}</td>
                    <td style={tdR}>{s.total_bookings}</td>
                    <td style={{ ...tdR, color:C.danger }}>{s.cancelled || 0}</td>
                    <td style={{ ...tdR, color:s.cancel_rate>20?C.danger:s.cancel_rate>10?C.amber:C.accent, fontWeight:'600' }}>
                      {s.cancel_rate.toFixed(1)}%
                    </td>
                    <td style={{ ...tdR, color:C.amber }}>{s.no_show || 0}</td>
                    <td style={{ ...tdR, color:s.noshow_rate>20?C.danger:s.noshow_rate>10?C.amber:C.accent, fontWeight:'600' }}>
                      {s.noshow_rate.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}

      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
};

export default AppointmentTracker;